import React, { useState, useContext } from 'react';
import { FaComment, FaTimes, FaPaperPlane, FaSpinner, FaStar, FaRegStar, FaHeadset, FaComments } from 'react-icons/fa';
import axios from 'axios';
import AuthContext from '../../context/AuthContext';

const FeedbackForm = () => {
  const { user } = useContext(AuthContext);
  const [isOpen, setIsOpen] = useState(false);
  const [activeTab, setActiveTab] = useState('feedback'); // 'feedback' or 'support'
  const [formData, setFormData] = useState({
    name: user?.name || '',
    email: user?.email || '',
    subject: '',
    message: '',
    rating: 0,
    type: 'feedback' // 'feedback' or 'support'
  });
  const [status, setStatus] = useState('idle'); // idle, submitting, success, error
  const [error, setError] = useState('');

  const toggleForm = () => {
    setIsOpen(!isOpen);
    // Reset form state when opening
    if (!isOpen) {
      setStatus('idle');
      setError('');
    }
  };
  
  const switchTab = (tab) => {
    setActiveTab(tab);
    setFormData({
      ...formData,
      type: tab,
      subject: tab === 'support' ? 'Support Request' : '',
    });
    setStatus('idle');
    setError('');
  };

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData({
      ...formData,
      [name]: value
    });
  };

  const handleRatingClick = (rating) => {
    setFormData({
      ...formData,
      rating
    });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    // Validate form
    if (!formData.name || !formData.email || !formData.message) {
      setError('Please fill in all required fields');
      return;
    }
    
    try {
      setStatus('submitting');
      setError('');
      
      // Send feedback or support request via API
      await axios.post('/api/feedback', {
        ...formData,
        userAgent: navigator.userAgent,
        timestamp: new Date().toISOString()
      });
      
      // Show success message
      setStatus('success');
      
      // Reset form after 3 seconds
      setTimeout(() => {
        setFormData({
          name: user?.name || '',
          email: user?.email || '',
          subject: '',
          message: '',
          rating: 0,
          type: activeTab
        });
        setStatus('idle');
        setIsOpen(false);
      }, 3000);
      
    } catch (err) {
      console.error(`Error submitting ${activeTab}:`, err);
      setStatus('error');
      setError(err.response?.data?.msg || `Failed to submit ${activeTab}. Please try again.`);
    }
  };

  const renderStars = () => {
    const stars = [];
    for (let i = 1; i <= 5; i++) {
      stars.push(
        <span 
          key={i} 
          onClick={() => handleRatingClick(i)}
          style={{ cursor: 'pointer', marginRight: '5px' }}
        >
          {i <= formData.rating ? <FaStar color="#D2BA7B" /> : <FaRegStar />}
        </span>
      );
    }
    return stars;
  };

  return (
    <div style={styles.container}>
      {/* Floating button */}
      <button 
        onClick={toggleForm} 
        style={styles.floatingButton}
        aria-label="Open feedback form"
        title="Provide Feedback"
      >
        {isOpen ? <FaTimes /> : <FaComment />}
      </button>
      
      {/* Feedback form */}
      {isOpen && (
        <div style={styles.formContainer}>
          <div style={styles.formHeader}>
            <h3 style={styles.formTitle}>{activeTab === 'feedback' ? 'Send Feedback' : 'Support Request'}</h3>
            <button 
              onClick={toggleForm} 
              style={styles.closeButton}
              aria-label="Close form"
            >
              <FaTimes />
            </button>
          </div>
          
          {/* Tabs */}
          <div style={styles.tabContainer}>
            <button 
              onClick={() => switchTab('feedback')} 
              style={{
                ...styles.tabButton,
                ...(activeTab === 'feedback' ? styles.activeTab : {})
              }}
            >
              <FaComments style={{marginRight: '5px'}} /> Feedback
            </button>
            <button 
              onClick={() => switchTab('support')} 
              style={{
                ...styles.tabButton,
                ...(activeTab === 'support' ? styles.activeTab : {})
              }}
            >
              <FaHeadset style={{marginRight: '5px'}} /> Support
            </button>
          </div>
          
          {status === 'success' ? (
            <div style={styles.successMessage}>
              {activeTab === 'feedback' ? (
                <>
                  <p>Thank you for your feedback!</p>
                  <p>We appreciate your input and will use it to improve our service.</p>
                  <p style={{ marginTop: '10px', color: '#243444' }}>Please check the <strong>Development Roadmap</strong> to see what additional features and options we are planning to add.</p>
                </>
              ) : (
                <>
                  <p>Thank you for contacting support!</p>
                  <p>Your request has been received and our team will respond as soon as possible.</p>
                  <p style={{ marginTop: '10px', color: '#243444' }}>Support tickets are typically responded to within 24-48 business hours.</p>
                </>
              )}
            </div>
          ) : (
            <form onSubmit={handleSubmit} style={styles.form}>
              {error && <div style={styles.errorMessage}>{error}</div>}
              
              <div style={styles.formGroup}>
                <label htmlFor="name" style={styles.label}>Name *</label>
                <input
                  type="text"
                  id="name"
                  name="name"
                  value={formData.name}
                  onChange={handleInputChange}
                  style={styles.input}
                  required
                />
              </div>
              
              <div style={styles.formGroup}>
                <label htmlFor="email" style={styles.label}>Email *</label>
                <input
                  type="email"
                  id="email"
                  name="email"
                  value={formData.email}
                  onChange={handleInputChange}
                  style={styles.input}
                  required
                />
              </div>
              
              <div style={styles.formGroup}>
                <label htmlFor="subject" style={styles.label}>Subject{activeTab === 'support' && ' *'}</label>
                <input
                  type="text"
                  id="subject"
                  name="subject"
                  value={formData.subject}
                  onChange={handleInputChange}
                  style={styles.input}
                  required={activeTab === 'support'}
                  placeholder={activeTab === 'support' ? 'Brief description of your issue' : ''}
                />
              </div>
              
              <div style={styles.formGroup}>
                <label htmlFor="message" style={styles.label}>Message *</label>
                <textarea
                  id="message"
                  name="message"
                  value={formData.message}
                  onChange={handleInputChange}
                  style={styles.textarea}
                  rows="4"
                  required
                />
              </div>
              
              {activeTab === 'feedback' && (
                <div style={styles.formGroup}>
                  <label style={styles.label}>How would you rate our app?</label>
                  <div style={styles.ratingContainer}>
                    {renderStars()}
                  </div>
                </div>
              )}
              
              <button 
                type="submit" 
                style={styles.submitButton}
                disabled={status === 'submitting'}
              >
                {status === 'submitting' ? (
                  <>
                    <FaSpinner style={styles.spinner} /> Sending...
                  </>
                ) : (
                  <>
                    <FaPaperPlane /> {activeTab === 'feedback' ? 'Send Feedback' : 'Submit Support Request'}
                  </>
                )}
              </button>
            </form>
          )}
        </div>
      )}
    </div>
  );
};

// Styles
const styles = {
  tabContainer: {
    display: 'flex',
    borderBottom: '1px solid #e0e0e0',
  },
  tabButton: {
    flex: 1,
    padding: '12px 15px',
    backgroundColor: '#f5f5f5',
    border: 'none',
    borderBottom: '3px solid transparent',
    cursor: 'pointer',
    fontWeight: '500',
    fontSize: '14px',
    color: '#4C5454',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    transition: 'all 0.2s ease',
  },
  activeTab: {
    backgroundColor: '#FFFAFA',
    borderBottom: '3px solid #D2BA7B',
    color: '#243444',
  },
  container: {
    position: 'fixed',
    bottom: '30px',
    right: '30px',
    zIndex: 1000
  },
  floatingButton: {
    width: '60px',
    height: '60px',
    borderRadius: '50%',
    backgroundColor: '#243444',
    color: '#FFFAFA',
    border: 'none',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    fontSize: '24px',
    boxShadow: '0 4px 8px rgba(0,0,0,0.2)',
    cursor: 'pointer',
    transition: 'all 0.3s ease',
    outline: 'none'
  },
  formContainer: {
    position: 'absolute',
    bottom: '80px',
    right: '0',
    width: '350px',
    backgroundColor: '#FFFAFA',
    borderRadius: '8px',
    boxShadow: '0 4px 12px rgba(0,0,0,0.15)',
    overflow: 'hidden',
    transition: 'all 0.3s ease'
  },
  formHeader: {
    backgroundColor: '#243444',
    color: '#FFFAFA',
    padding: '15px 20px',
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center'
  },
  formTitle: {
    margin: 0,
    fontSize: '18px',
    fontWeight: 'bold'
  },
  closeButton: {
    background: 'none',
    border: 'none',
    color: '#FFFAFA',
    fontSize: '16px',
    cursor: 'pointer'
  },
  form: {
    padding: '20px'
  },
  formGroup: {
    marginBottom: '15px'
  },
  label: {
    display: 'block',
    marginBottom: '5px',
    fontWeight: 'bold',
    color: '#243444',
    fontSize: '14px'
  },
  input: {
    width: '100%',
    padding: '10px',
    borderRadius: '4px',
    border: '1px solid #ccc',
    fontSize: '14px'
  },
  textarea: {
    width: '100%',
    padding: '10px',
    borderRadius: '4px',
    border: '1px solid #ccc',
    resize: 'vertical',
    fontSize: '14px'
  },
  ratingContainer: {
    display: 'flex',
    alignItems: 'center',
    fontSize: '20px'
  },
  submitButton: {
    backgroundColor: '#243444',
    color: '#FFFAFA',
    border: 'none',
    borderRadius: '4px',
    padding: '10px 15px',
    fontSize: '14px',
    fontWeight: 'bold',
    cursor: 'pointer',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    gap: '8px',
    width: '100%',
    marginTop: '10px'
  },
  errorMessage: {
    backgroundColor: '#ffebee',
    color: '#c62828',
    padding: '10px',
    borderRadius: '4px',
    marginBottom: '15px',
    fontSize: '14px'
  },
  successMessage: {
    padding: '20px',
    textAlign: 'center',
    color: '#243444'
  },
  spinner: {
    animation: 'spin 1s linear infinite',
    '@keyframes spin': {
      '0%': { transform: 'rotate(0deg)' },
      '100%': { transform: 'rotate(360deg)' }
    }
  }
};

export default FeedbackForm;
